// In-place editor for ONE standard contract sentence (admin only).
//
// Opened from the pencil on a specification line. It edits the company-wide
// template behind that line (org_settings.proposal_text.contract), not this
// contract's copy: every auto-generated contract follows it, and the open
// contract's line updates as you type (unless its list was edited by hand).
//
// Blank, or typed back to the built-in sentence, removes the override, so
// nothing can be lost.

import React, { useEffect, useRef, useState } from "react";
import {
  CONTRACT_TEXT_DEFAULTS,
  CONTRACT_TEXT_FILLINS,
  CONTRACT_TEXT_LABELS,
  CONTRACT_TEXT_MAX,
  contractTemplate,
  setContractLine,
  type ContractText,
  type ContractTextKey,
} from "../../shared/contractText";

type Props = {
  textKey: ContractTextKey;
  value: ContractText;
  onChange: (next: ContractText) => void;
  saveState?: "idle" | "saving" | "saved" | "error";
  /** This contract's list was edited by hand, so it won't follow the edit. */
  contractHandEdited?: boolean;
  onDone: () => void;
};

export default function StandardLineEditor({
  textKey,
  value,
  onChange,
  saveState,
  contractHandEdited,
  onDone,
}: Props) {
  // Raw text while typing, so emptying the box to retype doesn't snap back
  // to the default mid-edit.
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const text = draft ?? contractTemplate(value, textKey);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {}
  }, [textKey]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  });

  const edit = (raw: string) => {
    const t = raw.replace(/[\r\n]+/g, " ").slice(0, CONTRACT_TEXT_MAX);
    setDraft(t);
    onChange(setContractLine(value, textKey, t));
  };

  const edited =
    draft !== null
      ? draft.trim() !== "" && draft.trim() !== CONTRACT_TEXT_DEFAULTS[textKey]
      : value[textKey] !== undefined;
  const fillin = CONTRACT_TEXT_FILLINS[textKey];
  const lostFillin = fillin && text.trim() !== "" && !text.includes(fillin);

  const status =
    saveState === "saving"
      ? "Saving…"
      : saveState === "saved"
        ? "Saved"
        : saveState === "error"
          ? "Couldn't save — check your connection"
          : "";

  return (
    <div className="contract-std" role="group" aria-label="Standard wording">
      <div className="contract-std-head">
        <span className="contract-std-title">
          Standard wording · all future contracts
        </span>
        <span className="contract-std-used">{CONTRACT_TEXT_LABELS[textKey]}</span>
      </div>
      <textarea
        ref={ref}
        className="contract-std-input"
        rows={1}
        value={text}
        onChange={(e) => edit(e.target.value)}
        onBlur={() => setDraft(null)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            setDraft(null);
            onDone();
          }
        }}
        maxLength={CONTRACT_TEXT_MAX}
        spellCheck
        lang="en-US"
      />
      {fillin ? (
        <div className={`contract-std-note${lostFillin ? " is-warn" : ""}`}>
          {lostFillin ? (
            <>
              This line no longer has <code>{fillin}</code>, so the product
              name won't appear.
            </>
          ) : (
            <>
              <code>{fillin}</code> is filled in from the estimate.
            </>
          )}
        </div>
      ) : null}
      {contractHandEdited ? (
        <div className="contract-std-note">
          This contract's list was edited by hand, so it keeps its own text.
          Use “Reset to auto” to pick up the new wording.
        </div>
      ) : null}
      <div className="contract-std-actions">
        {status ? (
          <span
            className={`contract-std-status${saveState === "error" ? " is-error" : ""}`}
            aria-live="polite"
          >
            {status}
          </span>
        ) : null}
        {edited ? (
          <button
            type="button"
            className="contract-link-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setDraft(null);
              onChange(setContractLine(value, textKey, ""));
            }}
          >
            Reset to default
          </button>
        ) : null}
        <button
          type="button"
          className="contract-link-btn"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setDraft(null);
            onDone();
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
